import {
  GateTypes,
  RegFieldsEnum,
  RegistrationQuestion,
  RegistrationStep,
  RegistrationStepType,
} from '@/types/registration.types';

type Args = {
  newGateType: GateTypes | null;
  registrationSteps: RegistrationStep[];
  registrationQuestions: RegistrationQuestion[];
  requiredRegistrationQuestions: number[] | null | undefined;
  channelRegistrationQuestions: RegistrationQuestion[];
};

type Result = {
  registrationSteps: RegistrationStep[];
  registrationQuestions: RegistrationQuestion[];
  requiredRegistrationQuestions: number[];
};

/**
 * Reconciles the registration form's fields against the active gate type. Mirrors the
 * relevant rules from events-3 `useFieldRequired` / `useSetRequiredField`, scoped to
 * gating-driven concerns only. Returns updated arrays when changes are needed, or null
 * when the form is already in sync.
 *
 * Passcode field rules:
 *  - shared_passcode active → passcode field present and required
 *  - domain_email / password / no gate active → passcode field removed
 *  - passcode_list active → no-op (the audience-list editor owns that case)
 *
 * Email field rules (additive only):
 *  - domain_email active (allow or block) → email present and required
 *  - other gate types → unchanged (email may be required by other features outside gating;
 *    this helper never unmarks email since it doesn't know about those features)
 */
const applyGatingDerivedRegistrationFields = ({
  newGateType,
  registrationSteps,
  registrationQuestions,
  requiredRegistrationQuestions,
  channelRegistrationQuestions,
}: Args): Result | null => {
  if (newGateType === GateTypes.passcode_list) {
    return null;
  }

  // Start from the input arrays and accumulate changes.
  let nextSteps = registrationSteps;
  let nextQuestions = registrationQuestions;
  let nextRequired = requiredRegistrationQuestions ? [...requiredRegistrationQuestions] : [];
  let changed = false;

  // ---- Passcode field reconciliation ----
  const shouldHavePasscode = newGateType === GateTypes.shared_passcode;
  const hasPasscodeNow = registrationQuestions.some((q) => q.registration_question === RegFieldsEnum.passcode);

  if (shouldHavePasscode && !hasPasscodeNow) {
    const passcodeQuestion = channelRegistrationQuestions.find(
      (q) => q.registration_question === RegFieldsEnum.passcode
    );
    if (passcodeQuestion) {
      nextSteps = nextSteps.map((step) =>
        step.type === RegistrationStepType.general
          ? { ...step, questions: [...(step.questions ?? []), passcodeQuestion] }
          : step
      );
      nextQuestions = [...nextQuestions, passcodeQuestion];
      nextRequired = Array.from(new Set([...nextRequired, RegFieldsEnum.passcode]));
      changed = true;
    }
  } else if (!shouldHavePasscode && hasPasscodeNow) {
    nextSteps = nextSteps.map((step) =>
      step.type === RegistrationStepType.general
        ? {
            ...step,
            questions: (step.questions ?? []).filter((q) => q.registration_question !== RegFieldsEnum.passcode),
          }
        : step
    );
    nextQuestions = nextQuestions.filter((q) => q.registration_question !== RegFieldsEnum.passcode);
    nextRequired = nextRequired.filter((q) => q !== RegFieldsEnum.passcode);
    changed = true;
  }

  // ---- Email field reconciliation (additive only) ----
  // events-3 forces email-required when any domain gating is active. We only add — never
  // remove — because other features (profile / ticketing / social / etc.) may also require
  // email and this helper has no visibility into them.
  if (newGateType === GateTypes.domain_email) {
    const hasEmailQuestion = nextQuestions.some((q) => q.registration_question === RegFieldsEnum.email);
    const hasEmailRequired = nextRequired.includes(RegFieldsEnum.email);

    if (!hasEmailQuestion) {
      const emailQuestion = channelRegistrationQuestions.find((q) => q.registration_question === RegFieldsEnum.email);
      if (emailQuestion) {
        nextSteps = nextSteps.map((step) =>
          step.type === RegistrationStepType.general
            ? { ...step, questions: [...(step.questions ?? []), emailQuestion] }
            : step
        );
        nextQuestions = [...nextQuestions, emailQuestion];
        changed = true;
      }
    }

    if (!hasEmailRequired) {
      nextRequired = Array.from(new Set([...nextRequired, RegFieldsEnum.email]));
      changed = true;
    }
  }

  if (!changed) return null;

  return {
    registrationSteps: nextSteps,
    registrationQuestions: nextQuestions,
    requiredRegistrationQuestions: nextRequired,
  };
};

export default applyGatingDerivedRegistrationFields;
